import { Timestamp } from 'firebase/firestore';
import type { LunchConfiguration } from './lunch.model';
import type { Module, School } from './schools.model';
import type {
  LunchDiffRow,
  ModuleDiffRow,
  PricingDiff,
  SchoolYearPricing,
} from './school-pricing.model';

/** Ein Schuljahr beginnt am 01.08. (UTC), identisch zu SchoolyearService. */
export function schoolYearStartDate(schoolYear: number): Date {
  return new Date(Date.UTC(schoolYear, 7, 1));
}

/**
 * Erste Preisschiene einer neu angelegten Schule (#1076).
 *
 * Ohne Schiene laesst sich kein Modul speichern, und der Anlege-Dialog kann nur
 * bestehende Schienen kopieren — eine Schule ohne Schiene ist damit eine Sackgasse.
 * Sie startet direkt als `active`: der Entwurfsweg (draft → ready → active) gilt
 * fuer Folgejahre, das laufende Jahr einer neuen Schule wird sofort bepreist.
 */
export function createInitialPricing(
  schoolYear: number,
  createdBy: string,
  now: Timestamp = Timestamp.now(),
): SchoolYearPricing {
  return {
    schoolYear,
    status: 'active',
    validFrom: Timestamp.fromDate(schoolYearStartDate(schoolYear)),
    lunchConfigurations: [],
    createdOn: now,
    createdBy,
    activatedOn: now,
  };
}

export function findPricing(
  school: School | null | undefined,
  schoolYear: number,
): SchoolYearPricing | undefined {
  return school?.pricing?.find((p) => p.schoolYear === schoolYear);
}

export function getActivePricing(
  school: School | null | undefined,
): SchoolYearPricing | undefined {
  return school?.pricing?.find((p) => p.status === 'active');
}

export interface ResolveOptions {
  /** true im Mitarbeiter-Portal: auch draft und archived sind waehlbar. */
  includeDrafts?: boolean;
}

/**
 * Preisauflösung: eigene Schiene des Jahres, sonst Fallback auf die aktive.
 * Ohne includeDrafts zählen nur 'ready' und 'active' — Entwürfe und archivierte
 * Schienen sind für Eltern unsichtbar.
 */
export function resolvePricing(
  school: School | null | undefined,
  schoolYear: number,
  opts: ResolveOptions = {},
): SchoolYearPricing | undefined {
  const exact = findPricing(school, schoolYear);
  if (exact) {
    if (opts.includeDrafts) return exact;
    if (exact.status === 'ready' || exact.status === 'active') return exact;
  }
  return getActivePricing(school);
}

/** Schuljahr, aus dem die Module zu laden sind. null = Schule nicht migriert. */
export function resolvePricingYear(
  school: School | null | undefined,
  schoolYear: number,
  opts: ResolveOptions = {},
): number | null {
  return resolvePricing(school, schoolYear, opts)?.schoolYear ?? null;
}

/**
 * School-Kopie, deren lunchConfigurations aus der aufgelösten Schiene stammen.
 * Dadurch arbeiten getAvailableLunchConfigurations, shouldShowLunchStep und
 * shouldShowLunchSection unverändert weiter. Nicht migrierte Schulen bleiben
 * unverändert (Top-Level-Feld gilt weiter).
 */
export function resolveSchoolForYear(
  school: School,
  schoolYear: number,
  opts: ResolveOptions = {},
): School {
  const resolved = resolvePricing(school, schoolYear, opts);
  if (!resolved) return school;
  return { ...school, lunchConfigurations: resolved.lunchConfigurations };
}

/**
 * true, wenn für dieses Schuljahr keine eigene freigegebene Schiene existiert
 * und die Preise daher aus der aktiven Schiene stammen — im Anmeldeformular
 * der Auslöser für den Hinweis "Preise noch vorläufig".
 */
export function isPricingProvisional(
  school: School | null | undefined,
  schoolYear: number,
): boolean {
  if (!school) return false;
  const exact = findPricing(school, schoolYear);
  return !(exact?.status === 'ready' || exact?.status === 'active');
}

const moduleKey = (m: Module): string => m.sourceModuleId ?? m.id ?? '';

export function comparePricing(
  current: SchoolYearPricing | undefined,
  draft: SchoolYearPricing | undefined,
  currentModules: Module[],
  draftModules: Module[],
): PricingDiff {
  const byKey = new Map<string, { current?: Module; draft?: Module }>();
  for (const m of currentModules) {
    byKey.set(moduleKey(m), { ...(byKey.get(moduleKey(m)) ?? {}), current: m });
  }
  for (const m of draftModules) {
    byKey.set(moduleKey(m), { ...(byKey.get(moduleKey(m)) ?? {}), draft: m });
  }

  const modules: ModuleDiffRow[] = [...byKey.entries()].map(([key, pair]) => ({
    key,
    description: pair.draft?.description ?? pair.current?.description ?? '',
    current: pair.current,
    draft: pair.draft,
    currentFee: pair.current?.fee?.amount,
    draftFee: pair.draft?.fee?.amount,
    change: diffKind(
      pair.current?.fee?.amount,
      pair.draft?.fee?.amount,
      !!pair.current,
      !!pair.draft,
    ),
  }));
  modules.sort(
    (a, b) =>
      (a.draft?.index ?? a.current?.index ?? 0) -
      (b.draft?.index ?? b.current?.index ?? 0),
  );

  const lunchByKey = new Map<
    string,
    { current?: LunchConfiguration; draft?: LunchConfiguration }
  >();
  for (const c of current?.lunchConfigurations ?? []) {
    lunchByKey.set(c.name, { ...(lunchByKey.get(c.name) ?? {}), current: c });
  }
  for (const c of draft?.lunchConfigurations ?? []) {
    lunchByKey.set(c.name, { ...(lunchByKey.get(c.name) ?? {}), draft: c });
  }

  const lunch: LunchDiffRow[] = [...lunchByKey.entries()].map(([key, pair]) => ({
    key,
    name: pair.draft?.name ?? pair.current?.name ?? '',
    current: pair.current,
    draft: pair.draft,
    currentFee: pair.current?.fee?.amount,
    draftFee: pair.draft?.fee?.amount,
    change: diffKind(
      pair.current?.fee?.amount,
      pair.draft?.fee?.amount,
      !!pair.current,
      !!pair.draft,
    ),
  }));

  return {
    modules,
    lunch,
    hasChanges: [...modules, ...lunch].some((r) => r.change !== 'unchanged'),
  };
}

function diffKind(
  currentFee: number | undefined,
  draftFee: number | undefined,
  hasCurrent: boolean,
  hasDraft: boolean,
): ModuleDiffRow['change'] {
  if (!hasCurrent) return 'added';
  if (!hasDraft) return 'removed';
  return currentFee === draftFee ? 'unchanged' : 'changed';
}

/**
 * Schuljahr, dessen Preisschiene für ein Kind gilt (#1088): nach einer
 * Vertragsverlängerung zählt `currentSchoolYear` (wird dort hochgezählt),
 * `yearInfo.year` bleibt auf dem Anmeldejahr stehen. Portal und Functions
 * müssen dieselbe Quelle benutzen, sonst bietet das Portal Module an, die der
 * Server als „nicht zur Preisliste dieses Schuljahres gehörend" ablehnt.
 * Kopie in `functions/src/_shared/school-pricing.utils.ts` — Parity-Spec `apps/parity-tests/school-pricing.parity.spec.ts`.
 */
export function studentPricingYear(student: {
  currentSchoolYear?: number | null;
  yearInfo?: { year?: number | null } | null;
}): number | undefined {
  return student.currentSchoolYear ?? student.yearInfo?.year ?? undefined;
}
